import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api/certs';
function auth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } };
}

const REASON_LABELS = {
  unspecified:          'Unspecified',
  keyCompromise:        'Key Compromise',
  caCompromise:         'CA Compromise',
  affiliationChanged:   'Affiliation Changed',
  superseded:           'Superseded',
  cessationOfOperation: 'Cessation of Operation',
  certificateHold:      'Certificate Hold',
  removeFromCRL:        'Remove from CRL',
};

function daysBetween(a, b) {
  if (!a) return null;
  return Math.floor((new Date(a) - new Date(b || Date.now())) / 86400000);
}

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ExpiryBadge({ notAfter }) {
  if (!notAfter) return <span style={{ color: '#94a3b8' }}>—</span>;
  const days = daysBetween(notAfter, Date.now());
  const color = days < 0 ? '#dc2626' : days < 30 ? '#d97706' : '#16a34a';
  const bg    = days < 0 ? '#fee2e2' : days < 30 ? '#fef3c7' : '#dcfce7';
  const label = days < 0 ? `Expired ${-days}d ago` : days === 0 ? 'Expires today' : `${days}d left`;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color }}>
      {label}
    </span>
  );
}

export default function CertificateManagement({ user }) {
  const isAdmin = user?.role === 'admin';

  const [info,      setInfo]      = useState(null);
  const [registry,  setRegistry]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState({ text: '', type: '' });
  const [revokeSerial, setRevokeSerial] = useState('');
  const [revokeReason, setRevokeReason] = useState('unspecified');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revoking,  setRevoking]  = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [infoRes, regRes] = await Promise.all([
        axios.get(`${API}/info`, auth()),
        axios.get(`${API}/registry`, auth()),
      ]);
      setInfo(infoRes.data);
      setRegistry(regRes.data);
    } catch (e) {
      flash('Failed to load: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function revoke() {
    if (!revokeSerial.trim()) return flash('Enter serial number', 'error');
    setRevoking(true);
    try {
      const res = await axios.post(`${API}/revoke`, { serial: revokeSerial, reason: revokeReason }, auth());
      flash(res.data.message);
      setShowRevokeModal(false);
      setRevokeSerial('');
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setRevoking(false);
    }
  }


  async function unrevoke(serial) {
    try {
      const res = await axios.post(`${API}/unrevoke`, { serial }, auth());
      flash(res.data.message);
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    }
  }

  async function regenerateCRL() {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/regenerate-crl`, {}, auth());
      flash(res.data.message);
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setRegenerating(false);
    }
  }

  const crlStatus  = info?.crl?.status;
  const crlBadge   = crlStatus === 'valid'
    ? { color: '#16a34a', bg: '#dcfce7', label: '✔ Valid' }
    : crlStatus === 'expired'
    ? { color: '#dc2626', bg: '#fee2e2', label: '✘ Expired' }
    : { color: '#94a3b8', bg: '#f1f5f9', label: '— Not Generated' };

  const serverDays  = info?.server ? daysBetween(info.server.notAfter, Date.now()) : null;
  const caDays      = info?.ca     ? daysBetween(info.ca.notAfter,     Date.now()) : null;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">🔑 Certificate Management</div>
          <div className="page-subtitle">
            Self-signed CA · TLS certificates · CRL revocation control · Distribution point
          </div>
        </div>
        <div className="flex-gap">
          {isAdmin && (
            <>
              <button className="btn btn-ghost" onClick={regenerateCRL} disabled={regenerating}>
                {regenerating ? '⟳ Regenerating...' : '↻ Regenerate CRL'}
              </button>
              <button className="btn btn-primary" onClick={() => setShowRevokeModal(true)}>
                ✘ Revoke Certificate
              </button>
            </>
          )}
          <button className="refresh-btn" onClick={loadAll} disabled={loading}>↻ Refresh</button>
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}


      {/* KPI Cards */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        <div className={`card ${serverDays !== null && serverDays < 30 ? 'red' : 'green'}`}>
          <div className="card-label">Server Cert Expiry</div>
          <div className="card-value" style={{ fontSize: 18 }}>
            {serverDays !== null ? `${serverDays}d` : '—'}
          </div>
        </div>
        <div className={`card ${caDays !== null && caDays < 90 ? 'amber' : 'blue'}`}>
          <div className="card-label">CA Cert Expiry</div>
          <div className="card-value" style={{ fontSize: 18 }}>
            {caDays !== null ? `${caDays}d` : '—'}
          </div>
        </div>
        <div className={`card ${crlStatus === 'expired' ? 'red' : 'green'}`}>
          <div className="card-label">CRL Status</div>
          <div className="card-value" style={{ fontSize: 18 }}>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              background: crlBadge.bg, color: crlBadge.color }}>
              {crlBadge.label}
            </span>
          </div>
        </div>
        <div className={`card ${(info?.crl?.revokedCount || 0) > 0 ? 'amber' : 'green'}`}>
          <div className="card-label">Revoked Certs</div>
          <div className="card-value">{info?.crl?.revokedCount ?? '—'}</div>
        </div>
      </div>

      {loading ? <div className="loading">Loading certificate data...</div> : (
        <>
          {/* Two-column layout: CA info left, Server cert right */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

            {/* CA Certificate */}
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title"><div className="panel-title-icon">🏛</div>CA Certificate</div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                {info?.ca ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {[
                        ['Subject',   info.ca.subject],
                        ['Serial',    <code style={{ fontSize: 11 }}>{info.ca.serial}</code>],
                        ['Valid From',fmt(info.ca.notBefore)],
                        ['Valid To',  <><span style={{ marginRight: 8 }}>{fmt(info.ca.notAfter)}</span><ExpiryBadge notAfter={info.ca.notAfter} /></>],
                        ['Fingerprint', <code style={{ fontSize: 10, wordBreak: 'break-all' }}>{info.ca.fingerprint}</code>],
                      ].map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ color: '#94a3b8', paddingRight: 12, paddingBottom: 8, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                          <td style={{ paddingBottom: 8 }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty">No CA certificate found. Run generate-certs.js first.</div>
                )}
                <a href="/ca.crt" download="PatchManager-CA.crt" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}>
                  ⬇ Download CA cert
                </a>
              </div>
            </div>

            {/* Server Certificate */}
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title"><div className="panel-title-icon">🖥</div>Server Certificate</div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                {info?.server ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {[
                        ['Subject',   info.server.subject],
                        ['Serial',    <code style={{ fontSize: 11 }}>{info.server.serial}</code>],
                        ['Valid From',fmt(info.server.notBefore)],
                        ['Valid To',  <><span style={{ marginRight: 8 }}>{fmt(info.server.notAfter)}</span><ExpiryBadge notAfter={info.server.notAfter} /></>],
                        ['Fingerprint', <code style={{ fontSize: 10, wordBreak: 'break-all' }}>{info.server.fingerprint}</code>],
                      ].map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ color: '#94a3b8', paddingRight: 12, paddingBottom: 8, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                          <td style={{ paddingBottom: 8 }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty">No server certificate found.</div>
                )}
              </div>
            </div>
          </div>


          {/* CRL Info panel */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">
              <div className="panel-title"><div className="panel-title-icon">📋</div>CRL Distribution Point</div>
            </div>
            <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[
                { label: 'CRL Status',      value: <span style={{ fontWeight: 700, color: crlBadge.color }}>{crlBadge.label}</span> },
                { label: 'Last Generated',  value: fmt(info?.crl?.lastGenerated) },
                { label: 'Next Update',     value: fmt(info?.crl?.nextUpdate) },
              ].map(item => (
                <div key={item.label} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase' }}>{item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', gridColumn: '1/-1' }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase' }}>CDP URLs (embedded in certificates)</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <a href="/crl.pem" target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">⬇ Download CRL (PEM)</a>
                  <a href="/crl.der" download="crl.der" className="btn btn-ghost btn-sm">⬇ Download CRL (DER)</a>
                  <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center', fontFamily: 'monospace' }}>
                    {info?.crl?.cdpUrl}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Revoked certs table */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">✘</div>
                Revoked Certificates ({registry?.revokedCerts?.length || 0})
              </div>
            </div>
            {!registry?.revokedCerts?.length ? (
              <div className="empty" style={{ padding: 32 }}>No certificates have been revoked.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Subject</th>
                    <th>Reason</th>
                    <th>Revoked At</th>
                    <th>By</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {registry.revokedCerts.map(r => (
                    <tr key={r.serial}>
                      <td><code style={{ fontSize: 11 }}>{r.serial}</code></td>
                      <td style={{ fontSize: 12 }}>{r.subject}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: '#fee2e2', color: '#dc2626' }}>
                          {REASON_LABELS[r.reason] || r.reason}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{fmt(r.revokedAt)}</td>
                      <td style={{ fontSize: 12 }}>{r.revokedBy}</td>
                      {isAdmin && (
                        <td>
                          <button className="btn btn-sm btn-ghost" style={{ color: '#16a34a' }}
                            onClick={() => unrevoke(r.serial)}>
                            ↩ Unrevoke
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>


          {/* Issued certs table */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">✔</div>
                Issued Certificates ({registry?.issuedCerts?.length || 0})
              </div>
            </div>
            {!registry?.issuedCerts?.length ? (
              <div className="empty" style={{ padding: 32 }}>
                No certificate registry found. Run generate-certs.js to create certs and populate the registry.
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Subject</th>
                    <th>Purpose</th>
                    <th>Not Before</th>
                    <th>Not After</th>
                    <th>Status</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {registry.issuedCerts.map(c => {
                    const isRevoked = registry.revokedCerts?.some(r => r.serial === c.serial);
                    return (
                      <tr key={c.serial}>
                        <td><code style={{ fontSize: 11 }}>{c.serial}</code></td>
                        <td style={{ fontSize: 12 }}>{c.subject}</td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: c.purpose === 'ca' ? '#ede9fe' : '#dbeafe',
                            color:      c.purpose === 'ca' ? '#7c3aed' : '#1d4ed8' }}>
                            {c.purpose?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>{fmt(c.notBefore)}</td>
                        <td style={{ fontSize: 12 }}>
                          {fmt(c.notAfter)} <ExpiryBadge notAfter={c.notAfter} />
                        </td>
                        <td>
                          {isRevoked ? (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px',
                              borderRadius: 20, background: '#fee2e2', color: '#dc2626' }}>✘ Revoked</span>
                          ) : (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px',
                              borderRadius: 20, background: '#dcfce7', color: '#16a34a' }}>✔ Active</span>
                          )}
                        </td>
                        {isAdmin && (
                          <td>
                            {!isRevoked && c.purpose !== 'ca' && (
                              <button className="btn btn-sm btn-ghost" style={{ color: '#dc2626' }}
                                onClick={() => { setRevokeSerial(c.serial); setShowRevokeModal(true); }}>
                                ✘ Revoke
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}


      {/* Revoke Modal */}
      {showRevokeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 20 }}>
              ✘ Revoke Certificate
            </div>

            <div className="alert error" style={{ marginBottom: 16 }}>
              ⚠ Revoking a certificate immediately regenerates the CRL. Clients checking the CDP
              will reject this certificate within their next CRL refresh cycle.
            </div>

            <div className="form-group">
              <label className="form-label">Certificate Serial Number *</label>
              <input className="form-input" value={revokeSerial}
                onChange={e => setRevokeSerial(e.target.value)}
                placeholder="e.g. 02  (found in Issued Certificates table)" />
            </div>

            <div className="form-group">
              <label className="form-label">Revocation Reason</label>
              <select className="form-input" value={revokeReason}
                onChange={e => setRevokeReason(e.target.value)}>
                {Object.entries(REASON_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            <div className="flex-gap" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => { setShowRevokeModal(false); setRevokeSerial(''); }}>
                Cancel
              </button>
              <button className="btn btn-primary" style={{ background: '#dc2626' }}
                onClick={revoke} disabled={revoking || !revokeSerial.trim()}>
                {revoking ? '⟳ Revoking...' : '✘ Revoke & Update CRL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
